'use client';

import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Color } from '../../../../styles/Color';
import { Css, ITheme, Responsive, ResponsivePicture } from '../../../../ui';
import NoImage from '../../../../assets/icons/no-image.svg';
import { RouterLink } from '../../../../components/common/link/RouterLink';
import { PageRoutes } from '../../../app/pages/domain/constants/PageRoutes';
import { IGenericNewsItem } from '../domain/types/GenericNewsItem';
import { renderMarkdownAsPlainText } from '../../../../utils/renderMarkdownAsPlainText';
import { formatFullDate } from '../../../../i18n/formatFullDate';

interface IProps {
	items: IGenericNewsItem[];
	route: PageRoutes;
	isCompact?: boolean;
	displayDateAndTitleOnly?: boolean;
}

export default function NewsItemList({
	items,
	route,
	isCompact,
	displayDateAndTitleOnly,
}: IProps) {
	const isMobileXL = Responsive.useMatchesBreakpoint('mobileXL');
	const showCompactCard = isCompact || isMobileXL;
	const summaryLength = showCompactCard ? 150 : 400;

	return (
		<NewslettersWrapper>
			{items.map((item) => {
				const { id, slug, image, summary, title, publicationDate, createdAt } =
					item;
				const plainSummary = renderMarkdownAsPlainText(summary) as string;

				const date = publicationDate || createdAt;
				const formattedDate = formatFullDate(date, 'MMM d, yyyy');

				return (
					<NewsletterCard
						key={id}
						isCompact={showCompactCard}
						href={slug ? `${route}/item/${slug}` : ''}
					>
						<NewsletterImage
							className="img"
							source={image?.fields.file.url}
							isCompact={showCompactCard}
						/>

						<div className="title">
							{displayDateAndTitleOnly ? formattedDate : title}
						</div>

						{displayDateAndTitleOnly && (
							<div className="summary truncate">{title}</div>
						)}

						{plainSummary && !displayDateAndTitleOnly && (
							<div className={showCompactCard ? 'summary truncate' : 'summary'}>
								{showCompactCard
									? plainSummary
									: plainSummary.substring(0, summaryLength)}

								{!showCompactCard && summary.length > summaryLength && (
									<span className="read-more">Read more...</span>
								)}
							</div>
						)}
					</NewsletterCard>
				);
			})}
		</NewslettersWrapper>
	);
}

interface IImageProps {
	source?: string;
	isCompact?: boolean;
	className: string;
}

function NewsletterImage({ source, className, isCompact }: IImageProps) {
	if (!source) {
		return (
			<NoImageWrap className={className} isCompact={isCompact}>
				<NoImage />
			</NoImageWrap>
		);
	}
	return (
		<ResponsivePicture
			className={className}
			source={source}
			rounded
			objectFitCover
		/>
	);
}

const NoImageWrap = styled.div<{ isCompact?: boolean }, ITheme>`
	object-fit: cover;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: ${Color.Neutral2};
	border: 1px solid ${Color.BorderCard};
	border-radius: 8px;
	aspect-ratio: 1 / 1;

	svg {
		height: 28px;
		width: 28px;
	}
`;

const NewslettersWrapper = styled.div`
	display: flex;
	flex-direction: column;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: 0;
	}
`;

const NewsletterCard = styled(RouterLink, {
	shouldForwardProp: (prop) => prop !== 'isCompact',
})<{
	isCompact?: boolean;
}>`
	color: currentColor;
	display: grid;
	grid-template-columns: ${Css.pxToRem(120)} 1fr;
	gap: 0 ${Css.pxToRem(32)};
	grid-template-areas:
		'img title'
		'img summary';
	padding: ${Css.pxToRem(24)} 0;
	text-decoration: none;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		grid-template-columns: ${Css.pxToRem(80)} 1fr;
		gap: 0 ${Css.pxToRem(16)};
	}

	.img {
		grid-area: img;
		height: 120px;
		width: 120px;

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			height: 80px;
			width: 80px;
		}

		img {
			aspect-ratio: 1 / 1;
			height: 120px;
			width: 120px;

			@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
				height: 80px;
				width: 80px;
			}
		}
	}

	.title {
		grid-area: title;
		font-size: ${Css.pxToRem(14)};
		font-weight: 500;
		align-self: end;
		margin-bottom: ${Css.pxToRem(8)};

		overflow: hidden;
		display: -webkit-box;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
	}
	.summary {
		grid-area: summary;
		font-size: ${Css.pxToRem(16)};
		font-weight: 300;
		line-height: ${Css.pxToRem(24)};
	}
	.truncate {
		overflow: hidden;
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
	}
	.read-more {
		font-weight: 400;
		text-decoration: underline;
		padding-inline-start: ${Css.pxToRem(4)};
	}
	:not(:last-of-type) {
		border-bottom: 1px solid ${Color.BorderCard};
	}

	${({ isCompact }) =>
		isCompact &&
		css`
			grid-template-columns: ${Css.pxToRem(100)} 1fr;
			gap: 0 ${Css.pxToRem(16)};
			padding: ${Css.pxToRem(16)} 0;

			.img {
				display: flex;
				align-items: center;
				height: 100px;
				width: 100px;

				img {
					aspect-ratio: 1 / 1;
					height: 100px;
					width: 100px;

					@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
						height: 80px;
						width: 80px;
					}
				}
			}

			${NoImageWrap} {
				aspect-ratio: 1 / 1;

				@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
					height: 80px;
					width: 80px;
				}
			}
		`}
`;
